/**
 * WorkersTab - Gestionare Angajați și Subcontractori
 * Actualizat pentru Multi-Tenancy și Vizualizare Documente
 */
import { useState, useEffect } from "react";
import axios from "axios";
import { safeOpenHTMLWindow } from "@/utils/safeHTML";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { ScrollArea } from "@/components/ui/scroll-area";
import { toast } from "sonner";
import { 
  Users, UserPlus, Trash2, Phone, Mail, MapPin, ChevronDown, ChevronRight, 
  Building2, Eye, EyeOff, FileText, Download, Calendar, Shield, FileSignature,
  Palmtree, Car, User, Hash, Edit
} from "lucide-react";
import { checkAndDeductCredits } from "@/utils/creditSystem";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function WorkersTab({ workers, subcontractors, onRefresh, companyId, userId, documents }) {
  const [openWorkerDialog, setOpenWorkerDialog] = useState(false);
  const [openSubcontractorDialog, setOpenSubcontractorDialog] = useState(false);
  const [openSubEmployeeDialog, setOpenSubEmployeeDialog] = useState(false);
  const [selectedSubcontractor, setSelectedSubcontractor] = useState(null);
  const [expandedSubcontractors, setExpandedSubcontractors] = useState([]);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  
  // State pentru vizualizare profil și documente angajat
  const [selectedWorker, setSelectedWorker] = useState(null);
  const [workerDocuments, setWorkerDocuments] = useState([]);
  
  // State pentru editare angajat
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [editingWorker, setEditingWorker] = useState(null);
  const [showEditPassword, setShowEditPassword] = useState(false);
  
  const [newWorker, setNewWorker] = useState({ name: "", email: "", phone: "", password: "", home_address: "", is_administrator: false });
  const [newSubcontractor, setNewSubcontractor] = useState({ name: "", contact_person: "", phone: "", email: "" });
  const [newSubEmployee, setNewSubEmployee] = useState({ name: "", phone: "", specialization: "" });

  // Când se selectează un angajat, filtrăm documentele asociate
  useEffect(() => {
    if (selectedWorker && documents) {
      const workerDocs = documents.filter(doc => 
        doc.worker_id === selectedWorker.id || 
        doc.user_id === selectedWorker.id ||
        doc.user_name?.toLowerCase() === selectedWorker.name?.toLowerCase()
      );
      setWorkerDocuments(workerDocs);
    }
  }, [selectedWorker, documents]);

  const handleViewWorkerProfile = (worker) => {
    setSelectedWorker(worker);
  };

  const docTypeIcons = {
    leave_request: Palmtree,
    trip_sheet: Car,
    contract: FileSignature,
    ssm_psi: Shield,
    other: FileText
  };

  const docTypeLabels = {
    leave_request: "Cerere Concediu",
    trip_sheet: "Foaie Parcurs",
    contract: "Contract",
    ssm_psi: "Fișă SSM/PSI",
    other: "Document"
  };

  const docTypeColors = {
    leave_request: "bg-green-100 text-green-700",
    trip_sheet: "bg-blue-100 text-blue-700",
    contract: "bg-purple-100 text-purple-700",
    ssm_psi: "bg-amber-100 text-amber-700",
    other: "bg-slate-100 text-slate-700"
  };

  const viewDocument = (doc) => {
    if (doc.html_content) {
      safeOpenHTMLWindow(doc.html_content);
    } else if (doc.file_url) {
      window.open(doc.file_url, '_blank');
    }
  };

  const handleAddWorker = async (e) => {
    e.preventDefault();
    
    if (!companyId) {
      toast.error("Sesiune expirată. Vă rugăm să vă re-logați.");
      return;
    }
    
    if (!newWorker.name || !newWorker.password) {
      toast.error("Completați numele și parola!");
      return;
    }
    
    // Verifică și deduce creditele pentru adăugare angajat
    const creditResult = await checkAndDeductCredits(
      companyId,
      'add_employee',
      null,
      (warning) => toast.warning(warning, { duration: 8000 }),
      (block) => toast.error(block, { duration: 8000 })
    );
    
    if (!creditResult.canProceed) {
      return;
    }
    
    if (creditResult.usedBonus) {
      toast.warning("⚠️ Ați folosit documentul bonus gratuit!", { duration: 5000 });
    }
    
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/create-worker`, {
        name: newWorker.name,
        email: newWorker.email || null,
        phone: newWorker.phone || null,
        password: newWorker.password,
        home_address: newWorker.home_address || "",
        is_administrator: newWorker.is_administrator || false,
        created_by: userId
      }, { params: { company_id: companyId } });
      
      const loginInfo = response.data.login_info;
      const roleMsg = newWorker.is_administrator ? " (Administrator)" : "";
      toast.success(
        <div>
          <p className="font-bold text-green-700">✓ Angajat înregistrat cu succes!{roleMsg}</p>
          <p className="text-sm mt-2">Date de autentificare:</p>
          <p className="text-sm font-mono bg-gray-100 p-1 rounded mt-1">Login: {loginInfo.email}</p>
          <p className="text-sm font-mono bg-gray-100 p-1 rounded">Parolă: {loginInfo.password}</p>
        </div>,
        { duration: 15000 }
      );
      
      setNewWorker({ name: "", email: "", phone: "", password: "", home_address: "", is_administrator: false });
      setOpenWorkerDialog(false);
      onRefresh();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || "Eroare la înregistrare angajat";
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteWorker = async (id, name) => {
    if (!confirm(`Sigur doriți să ștergeți angajatul "${name}"?`)) return;
    try {
      await axios.delete(`${API}/users/${id}`, { params: { company_id: companyId } });
      toast.success("Angajat șters!");
      onRefresh();
    } catch (error) {
      toast.error("Eroare la ștergere");
    }
  };

  // Deschide dialogul de editare
  const handleOpenEditDialog = (worker) => {
    setEditingWorker({
      id: worker.id,
      name: worker.name || "",
      email: worker.email || "",
      phone: worker.phone || "",
      home_address: worker.home_address || "",
      is_administrator: worker.is_administrator || false,
      new_password: "" // Câmp pentru parolă nouă (opțional)
    });
    setShowEditPassword(false);
    setOpenEditDialog(true);
  };

  // Salvează modificările angajatului
  const handleSaveEditWorker = async (e) => {
    e.preventDefault();
    if (!editingWorker) return;
    
    setLoading(true);
    try {
      const updateData = {
        name: editingWorker.name,
        email: editingWorker.email || null,
        phone: editingWorker.phone || null,
        home_address: editingWorker.home_address || "",
        is_administrator: editingWorker.is_administrator || false
      };
      
      // Adaugă parola doar dacă a fost completată
      if (editingWorker.new_password && editingWorker.new_password.length >= 6) {
        updateData.new_password = editingWorker.new_password;
      }
      
      await axios.put(`${API}/users/${editingWorker.id}`, updateData, { 
        params: { company_id: companyId } 
      });
      
      if (editingWorker.new_password) {
        toast.success(
          <div>
            <p className="font-bold text-green-700">✓ Angajat actualizat!</p>
            <p className="text-sm mt-1">Noile credențiale:</p>
            <p className="text-sm font-mono bg-gray-100 p-1 rounded mt-1">Login: {editingWorker.email || `worker_${editingWorker.id.slice(0,8)}`}</p>
            <p className="text-sm font-mono bg-gray-100 p-1 rounded">Parolă: {editingWorker.new_password}</p>
          </div>,
          { duration: 10000 }
        );
      } else {
        toast.success("Angajat actualizat cu succes!");
      }
      
      setOpenEditDialog(false);
      setEditingWorker(null);
      onRefresh();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || "Eroare la actualizare angajat";
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleAddSubcontractor = async (e) => {
    e.preventDefault();
    if (!companyId) {
      toast.error("Sesiune expirată. Vă rugăm să vă re-logați.");
      return;
    }
    try {
      await axios.post(`${API}/subcontractors`, newSubcontractor, { params: { company_id: companyId } });
      toast.success("Subcontractor adăugat!");
      setNewSubcontractor({ name: "", contact_person: "", phone: "", email: "" });
      setOpenSubcontractorDialog(false);
      onRefresh();
    } catch (error) {
      toast.error("Eroare la adăugare");
    }
  };

  const handleDeleteSubcontractor = async (id) => {
    if (!confirm("Sigur doriți să ștergeți acest subcontractor?")) return;
    try {
      await axios.delete(`${API}/subcontractors/${id}`, { params: { company_id: companyId } });
      toast.success("Subcontractor șters!");
      onRefresh();
    } catch (error) {
      toast.error("Eroare la ștergere");
    }
  };

  const handleAddSubEmployee = async (e) => {
    e.preventDefault();
    if (!selectedSubcontractor) return;
    try {
      await axios.post(`${API}/subcontractors/${selectedSubcontractor.id}/employees`, newSubEmployee);
      toast.success("Angajat subcontractor adăugat!");
      setNewSubEmployee({ name: "", phone: "", specialization: "" });
      setOpenSubEmployeeDialog(false);
      setSelectedSubcontractor(null);
      onRefresh();
    } catch (error) {
      toast.error("Eroare la adăugare");
    }
  };

  const handleDeleteSubEmployee = async (subId, empId) => {
    try {
      await axios.delete(`${API}/subcontractors/${subId}/employees/${empId}`);
      toast.success("Angajat șters!");
      onRefresh();
    } catch (error) {
      toast.error("Eroare la ștergere");
    }
  };

  const toggleSubcontractor = (id) => {
    setExpandedSubcontractors(prev => 
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const myWorkers = workers.filter(w => w.role === 'worker');

  return (
    <div className="space-y-6">
      {/* Angajații Proprii */}
      <Card className="border-0 shadow-lg">
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle style={{fontFamily: 'Space Grotesk, sans-serif'}}>Angajații Mei</CardTitle>
            <CardDescription>Angajații proprii care se pontează</CardDescription>
          </div>
          <Dialog open={openWorkerDialog} onOpenChange={setOpenWorkerDialog}>
            <DialogTrigger asChild>
              <Button 
                className="bg-green-600 hover:bg-green-700 text-white font-semibold shadow-md" 
                data-testid="add-worker-button"
              >
                <UserPlus className="w-4 h-4 mr-2" />
                Înregistrează Angajat Nou
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle className="text-green-700 flex items-center gap-2">
                  <UserPlus className="w-5 h-5" />
                  Înregistrare Angajat Nou
                </DialogTitle>
                <DialogDescription>
                  Creează un cont pentru un angajat nou în firma ta. Email-ul este opțional.
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleAddWorker} className="space-y-4">
                <div>
                  <Label htmlFor="worker-name">Nume Complet *</Label>
                  <Input
                    id="worker-name"
                    value={newWorker.name}
                    onChange={(e) => setNewWorker({...newWorker, name: e.target.value})}
                    placeholder="Ion Popescu"
                    required
                    data-testid="worker-name-input"
                  />
                </div>
                <div>
                  <Label htmlFor="worker-email">Email (opțional)</Label>
                  <Input
                    id="worker-email"
                    type="email"
                    value={newWorker.email}
                    onChange={(e) => setNewWorker({...newWorker, email: e.target.value})}
                    placeholder="ion.popescu@email.ro"
                    data-testid="worker-email-input"
                  />
                  <p className="text-xs text-slate-500 mt-1">Lasă gol dacă angajatul nu are email</p>
                </div>
                <div>
                  <Label htmlFor="worker-phone">Telefon (opțional)</Label>
                  <Input
                    id="worker-phone"
                    type="tel"
                    value={newWorker.phone}
                    onChange={(e) => setNewWorker({...newWorker, phone: e.target.value})}
                    placeholder="07XX XXX XXX"
                    data-testid="worker-phone-input"
                  />
                </div>
                <div>
                  <Label htmlFor="worker-address">Adresa Domiciliu (opțional)</Label>
                  <Input
                    id="worker-address"
                    value={newWorker.home_address}
                    onChange={(e) => setNewWorker({...newWorker, home_address: e.target.value})}
                    placeholder="Str. Exemplu nr. 10, Iași"
                    data-testid="worker-address-input"
                  />
                </div>
                <div>
                  <Label htmlFor="worker-password">Parolă *</Label>
                  <div className="relative">
                    <Input
                      id="worker-password"
                      type={showPassword ? "text" : "password"}
                      value={newWorker.password}
                      onChange={(e) => setNewWorker({...newWorker, password: e.target.value})}
                      placeholder="Minim 6 caractere"
                      required
                      minLength={6}
                      autoComplete="new-password"
                      data-testid="worker-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  <p className="text-xs text-slate-500 mt-1">Angajatul va folosi această parolă pentru a se loga</p>
                </div>
                
                {/* Checkbox Administrator */}
                <div className="flex items-center space-x-3 p-3 bg-amber-50 rounded-lg border border-amber-200">
                  <input
                    type="checkbox"
                    id="worker-is-admin"
                    checked={newWorker.is_administrator}
                    onChange={(e) => setNewWorker({...newWorker, is_administrator: e.target.checked})}
                    className="w-4 h-4 text-amber-600 rounded border-amber-300 focus:ring-amber-500"
                    data-testid="worker-is-admin-checkbox"
                  />
                  <div>
                    <Label htmlFor="worker-is-admin" className="cursor-pointer text-amber-800 font-medium">
                      Administrator firmă
                    </Label>
                    <p className="text-xs text-amber-600">
                      Bifează dacă este proprietarul/administratorul firmei. Va fi inclus în taxele lunare dar poate fi exclus de pe foaia de pontaj.
                    </p>
                  </div>
                </div>
                
                <Button 
                  type="submit" 
                  className="w-full bg-green-600 hover:bg-green-700"
                  disabled={loading}
                  data-testid="submit-worker-button"
                >
                  {loading ? "Se înregistrează..." : "✓ Înregistrează Angajat"}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </CardHeader>
        <CardContent>
          {myWorkers.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <Users className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>Nu aveți angajați înregistrați încă.</p>
              <p className="text-sm">Apăsați butonul verde pentru a adăuga primul angajat.</p>
            </div>
          ) : (
            <div className="grid gap-3">
              {myWorkers.map((worker) => (
                <div
                  key={worker.id}
                  className={`flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-4 bg-gradient-to-r from-slate-50 to-white rounded-lg border hover:shadow-md transition-shadow cursor-pointer ${worker.is_administrator ? 'ring-2 ring-amber-400' : ''}`}
                  data-testid={`worker-card-${worker.id}`}
                  onClick={() => handleViewWorkerProfile(worker)}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <div className={`w-10 h-10 flex-shrink-0 rounded-full flex items-center justify-center ${worker.is_administrator ? 'bg-amber-100' : 'bg-green-100'}`}>
                      <span className={`font-semibold ${worker.is_administrator ? 'text-amber-700' : 'text-green-700'}`}>
                        {worker.name?.charAt(0)?.toUpperCase()}
                      </span>
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-medium truncate">{worker.name}</p>
                        {worker.is_administrator && (
                          <Badge className="bg-amber-500 text-xs">Admin</Badge>
                        )}
                      </div>
                      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-slate-500">
                        {worker.email && (
                          <span className="flex items-center gap-1 truncate max-w-full">
                            <Mail className="w-3 h-3 flex-shrink-0" />
                            <span className="truncate">{worker.email}</span>
                          </span>
                        )}
                        {worker.phone && (
                          <span className="flex items-center gap-1">
                            <Phone className="w-3 h-3" />
                            {worker.phone}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center gap-2 sm:flex-nowrap sm:flex-shrink-0">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={(e) => { e.stopPropagation(); handleOpenEditDialog(worker); }}
                      className="flex-1 sm:flex-initial text-amber-600 border-amber-300 hover:bg-amber-50"
                      data-testid={`edit-worker-${worker.id}`}
                    >
                      <Edit className="w-4 h-4 mr-1" />
                      Editare
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={(e) => { e.stopPropagation(); handleViewWorkerProfile(worker); }}
                      className="flex-1 sm:flex-initial text-blue-600 border-blue-300 hover:bg-blue-50"
                      data-testid={`documente-worker-${worker.id}`}
                    >
                      <FileText className="w-4 h-4 mr-1" />
                      Documente
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => { e.stopPropagation(); handleDeleteWorker(worker.id, worker.name); }}
                      className="text-red-500 hover:text-red-700 hover:bg-red-50"
                      data-testid={`delete-worker-${worker.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Subcontractori */}
      <Card className="border-0 shadow-lg">
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle style={{fontFamily: 'Space Grotesk, sans-serif'}}>Subcontractori</CardTitle>
            <CardDescription>Firme partenere și echipele lor</CardDescription>
          </div>
          <Dialog open={openSubcontractorDialog} onOpenChange={setOpenSubcontractorDialog}>
            <DialogTrigger asChild>
              <Button variant="outline" data-testid="add-subcontractor-button">
                <Building2 className="w-4 h-4 mr-2" />
                Adaugă Subcontractor
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Subcontractor Nou</DialogTitle>
                <DialogDescription>Adaugă o firmă parteneră</DialogDescription>
              </DialogHeader>
              <form onSubmit={handleAddSubcontractor} className="space-y-4">
                <div>
                  <Label>Nume Firmă *</Label>
                  <Input
                    value={newSubcontractor.name}
                    onChange={(e) => setNewSubcontractor({...newSubcontractor, name: e.target.value})}
                    placeholder="SC Exemplu SRL"
                    required
                  />
                </div>
                <div>
                  <Label>Persoană Contact</Label>
                  <Input
                    value={newSubcontractor.contact_person}
                    onChange={(e) => setNewSubcontractor({...newSubcontractor, contact_person: e.target.value})}
                    placeholder="Nume contact"
                  />
                </div>
                <div>
                  <Label>Telefon</Label>
                  <Input
                    value={newSubcontractor.phone}
                    onChange={(e) => setNewSubcontractor({...newSubcontractor, phone: e.target.value})}
                    placeholder="07XX XXX XXX"
                  />
                </div>
                <div>
                  <Label>Email</Label>
                  <Input
                    type="email"
                    value={newSubcontractor.email}
                    onChange={(e) => setNewSubcontractor({...newSubcontractor, email: e.target.value})}
                    placeholder="contact@firma.ro"
                  />
                </div>
                <Button type="submit" className="w-full">Adaugă</Button>
              </form>
            </DialogContent>
          </Dialog>
        </CardHeader>
        <CardContent>
          {subcontractors.length === 0 ? (
            <p className="text-center py-8 text-slate-500">Nu aveți subcontractori.</p>
          ) : (
            <div className="space-y-2">
              {subcontractors.map((sub) => (
                <Collapsible key={sub.id} open={expandedSubcontractors.includes(sub.id)}>
                  <div className="border rounded-lg">
                    <CollapsibleTrigger asChild>
                      <div
                        className="flex items-center justify-between p-4 cursor-pointer hover:bg-slate-50"
                        onClick={() => toggleSubcontractor(sub.id)}
                      >
                        <div className="flex items-center gap-3">
                          {expandedSubcontractors.includes(sub.id) ? (
                            <ChevronDown className="w-4 h-4" />
                          ) : (
                            <ChevronRight className="w-4 h-4" />
                          )}
                          <Building2 className="w-5 h-5 text-blue-600" />
                          <div>
                            <p className="font-medium">{sub.name}</p>
                            <p className="text-sm text-slate-500">
                              {sub.contact_person} • {sub.employees?.length || 0} angajați
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedSubcontractor(sub);
                              setOpenSubEmployeeDialog(true);
                            }}
                          >
                            <UserPlus className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteSubcontractor(sub.id);
                            }}
                            className="text-red-500"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                      <div className="px-4 pb-4 space-y-2">
                        {sub.employees?.map((emp) => (
                          <div
                            key={emp.id}
                            className="flex items-center justify-between p-2 bg-slate-50 rounded"
                          >
                            <div>
                              <p className="font-medium text-sm">{emp.name}</p>
                              <p className="text-xs text-slate-500">
                                {emp.specialization} • {emp.phone}
                              </p>
                            </div>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteSubEmployee(sub.id, emp.id)}
                              className="text-red-500"
                            >
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    </CollapsibleContent>
                  </div>
                </Collapsible>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Dialog adăugare angajat subcontractor */}
      <Dialog open={openSubEmployeeDialog} onOpenChange={setOpenSubEmployeeDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Angajat Subcontractor</DialogTitle>
            <DialogDescription>
              Adaugă un angajat pentru {selectedSubcontractor?.name}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleAddSubEmployee} className="space-y-4">
            <div>
              <Label>Nume *</Label>
              <Input
                value={newSubEmployee.name}
                onChange={(e) => setNewSubEmployee({...newSubEmployee, name: e.target.value})}
                required
              />
            </div>
            <div>
              <Label>Specializare</Label>
              <Input
                value={newSubEmployee.specialization}
                onChange={(e) => setNewSubEmployee({...newSubEmployee, specialization: e.target.value})}
                placeholder="Ex: Electrician, Instalator"
              />
            </div>
            <div>
              <Label>Telefon</Label>
              <Input
                value={newSubEmployee.phone}
                onChange={(e) => setNewSubEmployee({...newSubEmployee, phone: e.target.value})}
              />
            </div>
            <Button type="submit" className="w-full">Adaugă</Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Dialog vizualizare profil și documente angajat */}
      <Dialog open={!!selectedWorker} onOpenChange={(open) => !open && setSelectedWorker(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-hidden">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                <span className="text-green-700 font-bold text-xl">
                  {selectedWorker?.name?.charAt(0)?.toUpperCase()}
                </span>
              </div>
              <div>
                <span className="text-xl">{selectedWorker?.name}</span>
                <p className="text-sm font-normal text-slate-500">Profil Angajat</p>
              </div>
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6">
            {/* Date personale */}
            <div className="p-4 bg-slate-50 rounded-lg">
              <h4 className="font-semibold text-slate-700 mb-3 flex items-center gap-2">
                <User className="w-4 h-4" />
                Date Personale
              </h4>
              <div className="grid grid-cols-2 gap-4 text-sm">
                {selectedWorker?.email && (
                  <div className="flex items-center gap-2">
                    <Mail className="w-4 h-4 text-slate-400" />
                    <span>{selectedWorker.email}</span>
                  </div>
                )}
                {selectedWorker?.phone && (
                  <div className="flex items-center gap-2">
                    <Phone className="w-4 h-4 text-slate-400" />
                    <span>{selectedWorker.phone}</span>
                  </div>
                )}
                {selectedWorker?.home_address && (
                  <div className="flex items-center gap-2 col-span-2">
                    <MapPin className="w-4 h-4 text-slate-400" />
                    <span>{selectedWorker.home_address}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Documente angajat */}
            <div>
              <h4 className="font-semibold text-slate-700 mb-3 flex items-center gap-2">
                <FileText className="w-4 h-4" />
                Documente ({workerDocuments.length})
              </h4>
              
              <ScrollArea className="h-[300px]">
                {workerDocuments.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    <FileText className="w-12 h-12 mx-auto mb-3 opacity-30" />
                    <p>Nu există documente pentru acest angajat</p>
                    <p className="text-xs mt-1">Documentele se pot adăuga din secțiunea Documente</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {workerDocuments.map((doc) => {
                      const IconComponent = docTypeIcons[doc.document_type] || FileText;
                      return (
                        <div
                          key={doc.id}
                          className="flex items-center justify-between p-3 bg-white border rounded-lg hover:shadow-sm transition-shadow"
                        >
                          <div className="flex items-center gap-3">
                            <div className={`p-2 rounded-lg ${docTypeColors[doc.document_type] || 'bg-slate-100'}`}>
                              <IconComponent className="w-4 h-4" />
                            </div>
                            <div>
                              <p className="font-medium text-sm">{doc.title}</p>
                              <div className="flex items-center gap-2 text-xs text-slate-500">
                                <Badge variant="outline" className="text-xs py-0">
                                  <Hash className="w-2 h-2 mr-1" />
                                  {doc.registration_number}
                                </Badge>
                                <span className="flex items-center gap-1">
                                  <Calendar className="w-3 h-3" />
                                  {new Date(doc.created_at).toLocaleDateString('ro-RO')}
                                </span>
                              </div>
                            </div>
                          </div>
                          <div className="flex gap-1">
                            {(doc.html_content || doc.file_url) && (
                              <>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => viewDocument(doc)}
                                  className="text-blue-600"
                                >
                                  <Eye className="w-4 h-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => {
                                    if (doc.file_url) window.open(doc.file_url, '_blank');
                                  }}
                                  className="text-violet-600"
                                >
                                  <Download className="w-4 h-4" />
                                </Button>
                              </>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </ScrollArea>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog editare angajat */}
      <Dialog open={openEditDialog} onOpenChange={setOpenEditDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="w-5 h-5 text-amber-600" />
              Editare Angajat
            </DialogTitle>
            <DialogDescription>
              Modificați datele angajatului sau resetați credențialele de acces
            </DialogDescription>
          </DialogHeader>
          
          {editingWorker && (
            <form onSubmit={handleSaveEditWorker} className="space-y-4">
              <div>
                <Label htmlFor="edit-name">Nume *</Label>
                <Input
                  id="edit-name"
                  value={editingWorker.name}
                  onChange={(e) => setEditingWorker({...editingWorker, name: e.target.value})}
                  placeholder="Ion Popescu"
                  required
                  data-testid="edit-worker-name"
                />
              </div>
              
              <div>
                <Label htmlFor="edit-email">Email (Login)</Label>
                <Input
                  id="edit-email"
                  type="email"
                  value={editingWorker.email}
                  onChange={(e) => setEditingWorker({...editingWorker, email: e.target.value})}
                  placeholder="ion.popescu@email.ro"
                  data-testid="edit-worker-email"
                />
                <p className="text-xs text-slate-500 mt-1">
                  Acest email va fi folosit pentru autentificare
                </p>
              </div>
              
              <div>
                <Label htmlFor="edit-phone">Telefon</Label>
                <Input
                  id="edit-phone"
                  type="tel"
                  value={editingWorker.phone}
                  onChange={(e) => setEditingWorker({...editingWorker, phone: e.target.value})}
                  placeholder="07XX XXX XXX"
                  data-testid="edit-worker-phone"
                />
              </div>
              
              <div>
                <Label htmlFor="edit-address">Adresa Domiciliu</Label>
                <Input
                  id="edit-address"
                  value={editingWorker.home_address}
                  onChange={(e) => setEditingWorker({...editingWorker, home_address: e.target.value})}
                  placeholder="Str. Exemplu nr. 10, Iași"
                  data-testid="edit-worker-address"
                />
              </div>
              
              <div className="border-t pt-4">
                <Label htmlFor="edit-password" className="flex items-center gap-2">
                  <Shield className="w-4 h-4 text-amber-600" />
                  Parolă Nouă (opțional)
                </Label>
                <div className="relative mt-1">
                  <Input
                    id="edit-password"
                    type={showEditPassword ? "text" : "password"}
                    value={editingWorker.new_password}
                    onChange={(e) => setEditingWorker({...editingWorker, new_password: e.target.value})}
                    placeholder="Lasă gol pentru a păstra parola actuală"
                    minLength={6}
                    autoComplete="new-password"
                    data-testid="edit-worker-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowEditPassword(!showEditPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  >
                    {showEditPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                <p className="text-xs text-amber-600 mt-1">
                  ⚠️ Completați doar dacă doriți să schimbați parola (min. 6 caractere)
                </p>
              </div>
              
              {/* Checkbox Administrator în editare */}
              <div className="flex items-center space-x-3 p-3 bg-amber-50 rounded-lg border border-amber-200">
                <input
                  type="checkbox"
                  id="edit-worker-is-admin"
                  checked={editingWorker.is_administrator}
                  onChange={(e) => setEditingWorker({...editingWorker, is_administrator: e.target.checked})}
                  className="w-4 h-4 text-amber-600 rounded border-amber-300 focus:ring-amber-500"
                  data-testid="edit-worker-is-admin-checkbox"
                />
                <div>
                  <Label htmlFor="edit-worker-is-admin" className="cursor-pointer text-amber-800 font-medium">
                    Administrator firmă
                  </Label>
                  <p className="text-xs text-amber-600">
                    Administratorii sunt incluși în taxele lunare dar pot fi excluși de pe documentul de pontaj.
                  </p>
                </div>
              </div>
              
              <div className="flex gap-3 pt-2">
                <Button 
                  type="button" 
                  variant="outline" 
                  className="flex-1"
                  onClick={() => setOpenEditDialog(false)}
                >
                  Anulează
                </Button>
                <Button 
                  type="submit" 
                  className="flex-1 bg-amber-600 hover:bg-amber-700"
                  disabled={loading}
                  data-testid="save-edit-worker"
                >
                  {loading ? "Se salvează..." : "✓ Salvează Modificări"}
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
